import React from 'react';
import { useWorkbook } from '@/context/WorkbookContext';

// Front Matter
import { TitlePage } from '@/pages/front-matter/TitlePage';
import { CopyrightPage } from '@/pages/front-matter/CopyrightPage';
import { HowToUsePage } from '@/pages/front-matter/HowToUsePage';
import { TableOfContents } from '@/pages/front-matter/TableOfContents';

// Section Dividers
import { SectionDivider } from '@/components/SectionDivider';

// Section I: Stability
import { Rule1Page } from '@/pages/section-1/Rule1Page';
import { Rule2Page } from '@/pages/section-1/Rule2Page';
import { Rule3Page } from '@/pages/section-1/Rule3Page';
import { Rule4Page } from '@/pages/section-1/Rule4Page';

// Section II: Control
import { Rule5Page } from '@/pages/section-2/Rule5Page';
import { Rule6Page } from '@/pages/section-2/Rule6Page';
import { Rule7Page } from '@/pages/section-2/Rule7Page';
import { Rule8Page } from '@/pages/section-2/Rule8Page';

// Section III: Growth
import { Rule9Page } from '@/pages/section-3/Rule9Page';
import { Rule10Page } from '@/pages/section-3/Rule10Page';
import { Rule11Page } from '@/pages/section-3/Rule11Page';

// Section IV: Healing & Purpose
import { Rule12Page } from '@/pages/section-4/Rule12Page';
import { Rule13Page } from '@/pages/section-4/Rule13Page';
import { Rule14Page } from '@/pages/section-4/Rule14Page';

// Section V: Capstone
import { LifeMoneyMapPage } from '@/pages/section-5/LifeMoneyMapPage';
import { CommitmentPage } from '@/pages/section-5/CommitmentPage';

// Section VI: Appendices
import { GlossaryPage } from '@/pages/appendices/GlossaryPage';
import { WeeklyCalculatorPage } from '@/pages/appendices/WeeklyCalculatorPage';
import { CheckInSheetsPage } from '@/pages/appendices/CheckInSheetsPage';
import { DebtTrackerPage } from '@/pages/appendices/DebtTrackerPage';
import { CreditChecklistPage } from '@/pages/appendices/CreditChecklistPage';
import { EmergencyTrackerPage } from '@/pages/appendices/EmergencyTrackerPage';
import { IrregularPlannerPage } from '@/pages/appendices/IrregularPlannerPage';
import { CrisisModePage } from '@/pages/appendices/CrisisModePage';
import { ResetPage } from '@/pages/appendices/ResetPage';

// Back Matter
import { ProgramDescriptionPage } from '@/pages/back-matter/ProgramDescriptionPage';
import { FacilitatorNotePage } from '@/pages/back-matter/FacilitatorNotePage';

// Section Divider Components
const Section1Divider = () => (
  <SectionDivider 
    number="I" 
    title="Stability" 
    subtitle="Building Your Foundation"
    description="Before you can grow, you need solid ground. This section helps you understand exactly where your money comes from and where it goes."
    color="section-stability"
  />
);

const Section2Divider = () => (
  <SectionDivider 
    number="II" 
    title="Control" 
    subtitle="Taking Charge of Your Money"
    description="Once you have stability, it's time to take control. Learn to prepare for the unexpected and break free from debt."
    color="section-control"
  />
);

const Section3Divider = () => (
  <SectionDivider 
    number="III" 
    title="Growth" 
    subtitle="Building Your Future"
    description="With stability and control in place, you can now focus on building wealth and making smart financial decisions."
    color="section-growth"
  />
);

const Section4Divider = () => (
  <SectionDivider 
    number="IV" 
    title="Healing, Purpose & Impact" 
    subtitle="Money and Meaning"
    description="Money isn't just numbers—it's connected to our stories, values, and dreams. This section helps you heal and find purpose."
    color="section-healing"
  />
);

const Section5Divider = () => (
  <SectionDivider 
    number="V" 
    title="Capstone" 
    subtitle="Your Life & Money Map"
    description="Bring everything together. Create your personalized plan for the next 90 days, 12 months, and beyond."
    color="section-capstone"
  />
);

const Section6Divider = () => (
  <SectionDivider 
    number="VI" 
    title="Appendices" 
    subtitle="Tools & Trackers"
    description="Reference materials, worksheets, and trackers to support your ongoing financial journey."
    color="section-appendix"
  />
);

const pageComponents = {
  // Front Matter
  'title': TitlePage,
  'copyright': CopyrightPage,
  'how-to-use': HowToUsePage,
  'toc': TableOfContents,
  
  // Section Dividers
  'section-1-divider': Section1Divider,
  'section-2-divider': Section2Divider,
  'section-3-divider': Section3Divider,
  'section-4-divider': Section4Divider,
  'section-5-divider': Section5Divider,
  'section-6-divider': Section6Divider,
  
  // Section I
  'rule-1': Rule1Page,
  'rule-2': Rule2Page,
  'rule-3': Rule3Page,
  'rule-4': Rule4Page,
  
  // Section II
  'rule-5': Rule5Page,
  'rule-6': Rule6Page,
  'rule-7': Rule7Page,
  'rule-8': Rule8Page,
  
  // Section III
  'rule-9': Rule9Page,
  'rule-10': Rule10Page,
  'rule-11': Rule11Page,
  
  // Section IV
  'rule-12': Rule12Page,
  'rule-13': Rule13Page,
  'rule-14': Rule14Page,
  
  // Section V
  'life-money-map': LifeMoneyMapPage,
  'commitment': CommitmentPage,
  
  // Section VI
  'glossary': GlossaryPage,
  'weekly-calculator': WeeklyCalculatorPage,
  'check-in-sheets': CheckInSheetsPage,
  'debt-tracker': DebtTrackerPage,
  'credit-checklist': CreditChecklistPage,
  'emergency-tracker': EmergencyTrackerPage,
  'irregular-planner': IrregularPlannerPage,
  'crisis-mode': CrisisModePage,
  'reset-page': ResetPage,
  
  // Back Matter
  'program-description': ProgramDescriptionPage,
  'facilitator-note': FacilitatorNotePage,
};

export const PageRenderer = () => {
  const { currentPage } = useWorkbook();
  
  const PageComponent = pageComponents[currentPage] || TitlePage;
  
  return (
    <div className="animate-fade-in">
      <PageComponent />
    </div>
  );
};
